import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import IvisNk9Logo from '../../assets/IvisNk9Logo.png';
import LoginBackground from '../../assets/LoginBackground.jpg';
import Footer from '../../components/Footer';
import { useAuthContext } from '../../context/AuthContext';
import Notification from '../../components/Notification';
import ChangePasswordModal from '../../components/ChangePasswordModal/ChangePasswordModal';
import { useSpinner } from '../../context/SpinnerContext';
import logger from '../../utils/logger';
import './LoginPage.css';

function LoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [loginResult, setLoginResult] = useState(null);
    const [invalidCredentials, setInvalidCredentials] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login, changePassword } = useAuthContext();
    const { showSpinner, hideSpinner } = useSpinner();

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        try {
            logger.info('Intento de login', { email }, 'LoginPage');
            showSpinner('Iniciando sesión...');
            const result = await login({ email, password });
            
            // Verificar si requiere cambio de contraseña
            if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
                setLoginResult(result);
                setShowChangePasswordModal(true);
                hideSpinner();
                return;
            }
    
            hideSpinner();
            showNotification('Login exitoso', 'success');
            
        } catch (error) {
            logger.error('Error en el login', error, null, 'LoginPage');
            hideSpinner();

            if (error.response && error.response.status !== 500) {
                setInvalidCredentials(true);
                setEmail('');
                setPassword('');
                showNotification(error.response.data.error, 'error');
            } else {
                showNotification(error.message || 'Error en el login', 'error');
            }
        }
    };

    const handleInputChange = (field, value) => {
        // Limpiar error de credenciales cuando el usuario empiece a escribir
        if (invalidCredentials) {
            setInvalidCredentials(false);
        }

        if (field === 'email') {
            setEmail(value.toLowerCase());
        } else if (field === 'password') {
            setPassword(value);
        }
    };

    const handleChangePassword = async (passwordData) => {
        try {
            showSpinner('Cambiando contraseña...');
            logger.info('Intento de cambio de contraseña', null, 'LoginPage');
            await changePassword({
                ...passwordData,
                session: loginResult.session,
                cognito_id: loginResult.cognito_id
            });

            setShowChangePasswordModal(false);
            setLoginResult(null);
            showNotification('Contraseña cambiada exitosamente', 'success');
            navigate('/');
        } catch (error) {
            logger.error('Error al cambiar contraseña', error, null, 'LoginPage');
            hideSpinner();
            showNotification(error.message || 'Error al cambiar contraseña', 'error');
        }
    };

    return (
        <motion.div
            className="login-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
        >
            <div className="login-container">
                {/* Left Section - Futuristic Car Dashboard (60% width) */}
                <motion.div
                    className="login-left-section"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {/* Background Image */}
                    <div
                        className="login-background"
                        style={{ backgroundImage: `url(${LoginBackground})` }}
                    >
                        {/* Dark Overlay for better contrast */}
                        <div className="login-background-overlay"></div>
                    </div>

                    {/* Digital Text Overlay */}
                    <motion.div
                        className="digital-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.0 }}
                    >
                        IVIS NK9
                    </motion.div>
                </motion.div>

                {/* Right Section - Login Form (40% width) */}
                <motion.div
                    className="login-right-section"
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                >
                    {/* Header with white background */}
                    <motion.div
                        className="login-header-container"
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <motion.header className="login-header">
                            <img src={IvisNk9Logo} alt="IVIS NK9 Logo" className="login-logo" />
                            <h1 className="login-title">Sistema Integral de Inspección Vehicular</h1>
                        </motion.header>
                    </motion.div>

                    {/* Login Form */}
                    <div className="login-form-container">
                        <motion.div
                            className="login-card"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.8 }}
                        >
                            <motion.h2
                                className="login-card-title"
                                initial={{ y: -10, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.6, delay: 1.0 }}
                            >
                                Iniciar sesión
                            </motion.h2>

                            <form onSubmit={handleSubmit} className="login-form">
                                <motion.div
                                    className="form-group"
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 1.2 }}
                                >
                                    <input
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        placeholder="Correo Electrónico"
                                        className="form-input"
                                        required
                                    />
                                </motion.div>

                                <motion.div
                                    className="form-group"
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 1.4 }}
                                >
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={password}
                                            onChange={(e) => handleInputChange('password', e.target.value)}
                                            placeholder="Contraseña"
                                            className="form-input form-input-with-icon"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle-btn"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                    <line x1="1" y1="1" x2="23" y2="23" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </motion.div>

                                <motion.button
                                    type="submit"
                                    className="login-button"
                                    disabled={!email || !password}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 1.6 }}
                                >
                                    Iniciar sesión
                                </motion.button>
                            </form>

                            <motion.p
                                className="login-instruction"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.6, delay: 2.0 }}
                            >
                                Haga clic en Iniciar sesión para entrar
                            </motion.p>
                        </motion.div>
                    </div>
                </motion.div>
            </div>

            {/* Footer Component */}
            <Footer />

            {/* Notification Component */}
            <Notification
                visible={notification.visible}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
            />

            {/* Change Password Modal */}
            <ChangePasswordModal
                isOpen={showChangePasswordModal}
                onClose={() => setShowChangePasswordModal(false)}
                onSubmit={handleChangePassword}
            />
        </motion.div>
    );
}

export default LoginPage;